import React from "react";
import styled from "styled-components";
import { Container } from "@mui/material";
import SEO from "../components/SEO";
import { settings } from "../data/settings";
import GuideLeadForm from "../components/GuideLeadForm";
import SeoInternalLinks from "../components/SeoInternalLinks";

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--my-color3);
`;

const SubTitle = styled.h2`
  font-size: 1.4rem;
  margin-top: 2rem;
  margin-bottom: 0.7rem;
  color: var(--my-color3);
`;

const Paragraph = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin: 0 0 1rem 1.2rem;
  padding: 0;
  line-height: 1.7;
`;

const CTA = styled.a`
  display: inline-block;
  margin-top: 0.6rem;
  margin-right: 0.75rem;
  text-decoration: none;
  background: var(--my-color3);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  text-transform: uppercase;
`;

const faqLd = [
  {
    "@type": "Question",
    name: "Combien coûte un site vitrine sur mesure ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Un site vitrine sur mesure se situe souvent entre 2 000 € et 7 000 € selon le nombre de pages, le niveau de design, les contenus et les intégrations.",
    },
  },
  {
    "@type": "Question",
    name: "Quels éléments font varier le prix ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Le cadrage, le design, les contenus, les intégrations techniques, la complexité SEO et la maintenance influencent fortement le budget final.",
    },
  },
  {
    "@type": "Question",
    name: "Combien de temps faut-il pour livrer un site ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "En général 3 à 8 semaines selon la complexité et la disponibilité des contenus.",
    },
  },
  {
    "@type": "Question",
    name: "Le SEO est-il inclus ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Un socle SEO propre doit être inclus dès la V1 : titres, métadonnées, structure, maillage interne et performance.",
    },
  },
  {
    "@type": "Question",
    name: "Peut-on faire évoluer le site ensuite ?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Oui, une V1 bien architecturée sert de base pour ajouter des pages, des fonctionnalités et des automatisations.",
    },
  },
];

export default function PrixSiteWebPage() {
  return (
    <Container
      sx={{ py: 4 }}
      style={{ maxWidth: "900px", marginTop: settings.navbar.height }}
    >
      <SEO
        title="Combien coûte un site web sur mesure en 2026 ? | DigiBuddy"
        description="Fourchettes de prix réalistes pour un site web sur mesure, facteurs qui font varier le budget, délais et conseils pour cadrer votre projet sans mauvaise surprise."
        keywords="combien coûte un site web sur mesure, prix site vitrine, budget création site internet, tarif développeur web freelance, coût refonte site web"
        url="https://www.digibuddy.fr/combien-coute-site-web-sur-mesure"
        canonical="https://www.digibuddy.fr/combien-coute-site-web-sur-mesure"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Accueil",
                item: "https://www.digibuddy.fr/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Combien coûte un site web sur mesure",
                item: "https://www.digibuddy.fr/combien-coute-site-web-sur-mesure",
              },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqLd,
          },
        ]}
      />

      <Title>Combien coûte un site web sur mesure en 2026&nbsp;?</Title>
      <Paragraph>
        Le prix dépend surtout de la complexité du projet et de ce que vous
        attendez comme résultat business : demandes entrantes, rendez-vous
        qualifiés, prise de contact en ligne. Ce guide pose des repères
        réalistes pour cadrer budget et périmètre.
      </Paragraph>

      <SubTitle>Fourchettes de prix usuelles</SubTitle>
      <List>
        <li>
          Site vitrine simple (5 à 8 pages) :{" "}
          <strong>2 000 € à 4 000 €</strong>
        </li>
        <li>
          Site vitrine avancé + socle SEO initial :{" "}
          <strong>4 000 € à 7 000 €</strong>
        </li>
        <li>
          Refonte complète avec contenus et parcours orienté conversion :{" "}
          <strong>7 000 € à 12 000 €</strong>
        </li>
      </List>

      <SubTitle>Ce qui fait vraiment varier le budget</SubTitle>
      <List>
        <li>Nombre de pages et niveau de personnalisation</li>
        <li>Travail UX/UI et qualité des contenus (rédaction, médias)</li>
        <li>Fonctionnalités (formulaires avancés, CRM, automatisations)</li>
        <li>SEO technique, architecture et maillage interne</li>
        <li>Maintenance et évolutions après mise en ligne</li>
      </List>

      <SubTitle>Comment éviter de dépasser le budget</SubTitle>
      <Paragraph>
        Définissez un objectif prioritaire (par exemple plus de leads), un
        périmètre clair pour la V1, puis planifiez les évolutions par étapes.
        Cette approche limite les risques et accélère la mise en ligne tout en
        gardant une vision produit.
      </Paragraph>

      <SubTitle>FAQ</SubTitle>
      <Paragraph>
        <strong>Combien coûte un site vitrine sur mesure&nbsp;?</strong>
        <br />
        Souvent entre 2 000 € et 7 000 € selon les pages, le design, les
        contenus et les intégrations.
      </Paragraph>
      <Paragraph>
        <strong>Quels éléments font varier le prix&nbsp;?</strong>
        <br />
        Cadrage, design, contenus, intégrations, SEO et maintenance sont les
        leviers principaux.
      </Paragraph>
      <Paragraph>
        <strong>Combien de temps faut-il pour livrer un site&nbsp;?</strong>
        <br />
        En général 3 à 8 semaines selon la complexité et la disponibilité des
        contenus.
      </Paragraph>
      <Paragraph>
        <strong>Le SEO est-il inclus&nbsp;?</strong>
        <br />
        Un socle SEO propre doit faire partie de la V1 (titres, meta, structure,
        maillage, performance).
      </Paragraph>
      <Paragraph>
        <strong>Peut-on faire évoluer le site ensuite&nbsp;?</strong>
        <br />
        Oui : une base saine permet d’ajouter pages, fonctionnalités et
        automatisations sans tout repartir de zéro.
      </Paragraph>

      <CTA href="/#contact">Demander une estimation</CTA>
      <CTA href="/creation-site-web-sur-mesure" style={{ background: "var(--my-color2)" }}>
        Offre sites web
      </CTA>

      <SeoInternalLinks
        links={[
          { to: "/freelance-ou-agence-pour-creer-son-site", label: "Freelance ou agence pour créer son site ?" },
          { to: "/creation-site-web-sur-mesure", label: "Création de site web sur mesure" },
          { to: "/", label: "Accueil DigiBuddy" },
        ]}
      />

      <GuideLeadForm source="article-prix-site-web" />
    </Container>
  );
}
